//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import { uploadContentLibraryItem } from '@apis/content-library/contentLibraryApi';
import {
  IContentLibraryUploadRequest,
  IContentLibraryUploadResponse,
} from '@apis/content-library/contentLibraryApiModel';
import {
  BYTES_PER_MB,
  LARGE_VIDEO_IMPORT_LIMIT_MB,
  NORMAL_VIDEO_IMPORT_LIMIT_MB,
} from '@content-drawer/contentDrawerConstants';
import { getIsLargeVideoUploadEnabled } from '@content-drawer/redux/getContentDrawer';
import {
  Upload,
  UploadEvent,
  useUploadContext,
} from '@content-drawer/upload/UploadContextProvider';
import { useCurrentStateAndParams } from '@hooks/useCurrentStateAndParams';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import CollectionId from '@models/CollectionId';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { isDefined } from '@utils/filterPredicates';
import uuid from '@utils/uuid';

const mediaItemsKeys = [QueryKeyConstants.MediaFilters];

interface UploadMutationRequest extends IContentLibraryUploadRequest {
  id: string;
}

const mutationFn = async (request: UploadMutationRequest) => {
  return await uploadContentLibraryItem(request);
};

export const useUploader = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { onUploadEvent, uploads } = useUploadContext();
  const {
    params: { collectionId: serializedCollectionId },
  } = useCurrentStateAndParams('library.collections.content');

  // If this hook is used in a collection, we want to be able to pass that collectionId
  // to the upload mutation to properly associate that uploaded item with the collection
  const collectionId = serializedCollectionId
    ? CollectionId(serializedCollectionId)
    : undefined;

  const isLargeVideoUploadEnabled = useSelector(getIsLargeVideoUploadEnabled);

  const invalidateQueries = async () => {
    // Invalidate the first page and display the newly uploaded media item to the user
    await queryClient.invalidateQueries(mediaItemsKeys, {
      refetchPage: (_, index) => index === 0,
    });
    // Invalidate other pages in the background
    queryClient.invalidateQueries(mediaItemsKeys, {
      refetchPage: (_, index) => index > 0,
    });
  };

  const dispatchSuccessNotification = () => {
    dispatch(
      toastNotificationsActions.addToastNotification({
        message: `Some videos may take time to process, but we'll let you know when they're ready.`,
        options: {
          toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
        },
        type: ToastType.SUCCESS,
        title: 'Almost done!',
      }),
    );
  };

  const parseResponse = (
    xhr: XMLHttpRequest,
  ): IContentLibraryUploadResponse | undefined => {
    try {
      return JSON.parse(xhr.response);
    } catch (error) {
      return undefined;
    }
  };

  const uploadMutation = useMutation<
    XMLHttpRequest,
    XMLHttpRequest,
    UploadMutationRequest
  >({
    mutationFn,
    onSuccess: async (xhr, request) => {
      const response = parseResponse(xhr);
      if (response) {
        const { id: resultId, videoId, videoStatus } = response;
        onUploadEvent(UploadEvent.COMPLETED, { id: request.id, resultId, xhr });

        await invalidateQueries();
        if (videoId && videoStatus) {
          dispatchSuccessNotification();
        }
      } else {
        onUploadEvent(UploadEvent.FAILED, {
          id: request.id,
          error:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        });

        await invalidateQueries();
      }
    },
    onError: (error, request) => {
      onUploadEvent(UploadEvent.FAILED, {
        id: request.id,
        error: error.responseText,
      });

      queryClient.invalidateQueries(mediaItemsKeys);
    },
  });

  const cancelUpload = (upload: Upload) => {
    if (upload.xhr) {
      upload.xhr.abort();
    }
    onUploadEvent(UploadEvent.CANCELED, upload);
  };

  const getMaxFileSizeMb = () => {
    return isLargeVideoUploadEnabled
      ? LARGE_VIDEO_IMPORT_LIMIT_MB
      : NORMAL_VIDEO_IMPORT_LIMIT_MB;
  };

  const uploadFile = async (file: File) => {
    const id = uuid();
    const maxFileSizeMb = getMaxFileSizeMb();
    onUploadEvent(UploadEvent.STARTED, { id, file });

    if (file.size > maxFileSizeMb * BYTES_PER_MB) {
      onUploadEvent(UploadEvent.FAILED, {
        id,
        error: `File size is too large. Please select a file under ${maxFileSizeMb}MB.`,
      });
      return;
    }

    try {
      await uploadMutation.mutateAsync({
        id,
        file,
        ...(isDefined(collectionId) && { collectionId }),
        uploadProgress: (xhr, progress) =>
          onUploadEvent(UploadEvent.IN_PROGRESS, { id, progress, xhr }),
      });
    } catch (error) {
      onUploadEvent(UploadEvent.FAILED, {
        id,
        error: 'An unexpected error occurred, please try again.',
      });
    }
  };

  return { cancelUpload, uploadFile, uploads };
};
